import React, { FC } from 'react'
import { SimpleContentProps } from './SimpleContent.types'
import { sanitizeHTML } from '../../../utils/common-utils'

import './SimpleContent.scss'

/**
 * SimpleContent component for displaying basic content blocks.
 *
 * @remarks
 * Renders a section with optional title, introduction, publish date/time, rich content, and a button or link.
 * Supports brand extension, vertical spacing, and background color customization.
 * Sanitizes HTML for title and content to prevent XSS.
 *
 * @param props - The properties for the SimpleContent component.
 * @param props.content - The content and configuration for the block.
 * @param props.testId - Optional test id for testing.
 * @returns The rendered SimpleContent component.
 */
export const SimpleContent: FC<SimpleContentProps> = props => {
  const {
    title,
    intro,
    publishDate,
    publishTime,
    content,
    button,
    brandExtension,
    vertSpacing,
    bgColor,
    link,
  } = props.content

  const { testId } = props

  const buildButton = () => {
    const btn = button || link
    const { linkHref, linkTarget, analytics, linkText, linkAltText } = btn
    return (
      <a
        className='Button Button--primary u-marginVert8'
        href={linkHref}
        target={linkTarget}
        aria-label={linkAltText || ''}
        data-analytics={analytics ?? ''}
      >
        {linkText}
      </a>
    )
  }

  return (
    <section
      className={`SimpleContent ${vertSpacing} ${bgColor} ${brandExtension}`}
      data-testid={testId ? `${testId}` : null}
    >
      <div className={`u-sizeConstrained ${bgColor?.length !== 0 && 'u-paddingVert48'}`}>
        <header>
          {title && <h1>{sanitizeHTML(title)}</h1>}
          {(intro || publishDate) && (
            <div className='SimpleContent-subtitle u-marginBottom32 u-textSizeMsrp1 u-textDefault'>
              {intro && <p className='SimpleContent-introduction'>{intro}</p>}
              {publishDate && (
                <time className='SimpleContent-publishDate' dateTime={publishTime}>
                  {publishDate}
                </time>
              )}
            </div>
          )}
        </header>

        {content && (
          <div className='Content' dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }} />
        )}
        {button && buildButton()}
        {link && buildButton()}
      </div>
    </section>
  )
}
